/**
 * Utility functions for working with translations.
 * Provides type-safe helpers for building translation keys, pluralization,
 * and language display names.
 */

import i18next from 'i18next';
import { LANGUAGE_NAMES, type Language } from './types';
import type { Locale } from 'date-fns';
import { enGB, fr, nl } from 'date-fns/locale';

/**
 * Type-safe translation key builder.
 * Constructs nested keys using dot notation.
 *
 * @param parts - Array of key segments
 * @returns A dot-separated translation key
 *
 * @example
 * ```typescript
 * const key = buildKey(['wellness', 'categories', 'sleep']);
 * // Returns: 'wellness.categories.sleep'
 *
 * const translated = t(buildKey(['common', 'actions', 'save']));
 * // Translates: 'common.actions.save'
 * ```
 */
export const buildKey = (parts: readonly string[]): string => {
  return parts.filter(Boolean).join('.');
};

/**
 * Get translation or return the key if translation is missing.
 * Useful for graceful degradation when translations are incomplete.
 *
 * @param key - The translation key
 * @param defaultValue - Optional default value if translation missing
 * @returns The translated string or the key/default
 *
 * @example
 * ```typescript
 * const text = getTranslationOrKey('wellness.title');
 * // Returns translation or 'wellness.title' if missing
 *
 * const text = getTranslationOrKey('missing.key', 'Fallback Text');
 * // Returns 'Fallback Text' if translation missing
 * ```
 */
export const getTranslationOrKey = (
  key: string,
  defaultValue?: string,
): string => {
  const translation = i18next.t(key);

  // i18next returns the key if translation is missing
  if (translation === key) {
    return defaultValue ?? key;
  }

  return translation;
};

/**
 * Pluralization helper for translation keys.
 * Generates the correct plural form based on count.
 *
 * @param key - Base translation key
 * @param count - The count to determine plural form
 * @returns The pluralized translation
 *
 * @example
 * ```typescript
 * // Assuming translation keys:
 * // "items_one": "{{count}} item"
 * // "items_other": "{{count}} items"
 *
 * const text = pluralize('items', 1);
 * // Returns: '1 item'
 *
 * const text = pluralize('items', 5);
 * // Returns: '5 items'
 * ```
 */
export const pluralize = (key: string, count: number): string => {
  return i18next.t(key, { count });
};

/**
 * Get the display name for a language code.
 * Returns the human-readable name (e.g., 'English', 'Nederlands').
 *
 * @param language - The language code
 * @returns The display name for the language
 *
 * @example
 * ```typescript
 * const name = getLanguageName('en');
 * // Returns: 'English'
 *
 * const name = getLanguageName('nl');
 * // Returns: 'Nederlands'
 * ```
 */
export const getLanguageName = (language: Language): string => {
  return LANGUAGE_NAMES[language];
};

/**
 * Format a translation key for debugging purposes.
 * Useful for displaying keys in development mode.
 *
 * @param namespace - The namespace
 * @param key - The key within the namespace
 * @returns Formatted key string
 *
 * @example
 * ```typescript
 * const formatted = formatKey('wellness', 'title');
 * // Returns: '[wellness:title]'
 * ```
 */
export const formatKey = (namespace: string, key: string): string => {
  return `[${namespace}:${key}]`;
};

/**
 * Check if a translation key exists.
 * Useful for conditional rendering based on translation availability.
 *
 * @param key - The translation key to check
 * @param namespace - Optional namespace (defaults to current)
 * @returns True if the translation exists
 *
 * @example
 * ```typescript
 * if (translationExists('wellness.advanced.feature')) {
 *   return <AdvancedFeature />;
 * }
 * ```
 */
export const translationExists = (
  key: string,
  namespace?: string,
): boolean => {
  const fullKey = namespace ? `${namespace}:${key}` : key;
  return i18next.exists(fullKey);
};

/**
 * BCP 47 locale for Intl APIs (toLocaleString and friends) for the given app
 * language. Maps to Belgian/European variants so dates render in day-first
 * order for every supported language.
 */
export const getIntlLocale = (language: string): string => {
  switch (language) {
    case 'nl':
      return 'nl-BE';
    case 'fr':
      return 'fr-BE';
    default:
      return 'en-GB';
  }
};

/**
 * date-fns locale for the given app language. date-fns' `format()` renders
 * month/weekday names in English unless a locale is passed explicitly.
 */
export const getDateFnsLocale = (language: string): Locale => {
  switch (language) {
    case 'nl':
      return nl;
    case 'fr':
      return fr;
    default:
      return enGB;
  }
};
